import { useState, useRef, useCallback } from "react";
import { API_BASE } from "../config";

const STATUS = {
  pending: { label: "Queued", color: "#4a5568" },
  uploading: { label: "Uploading", color: "#d4a017" },
  processing: { label: "Processing", color: "#3b82f6" },
  done: { label: "Indexed", color: "#22c55e" },
  error: { label: "Failed", color: "#ef4444" },
};

const BATCH_SIZE = 50;
const ALLOWED = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/jpeg", "image/png", "image/webp", "text/markdown", "text/plain"];
const ALLOWED_EXT = [".pdf", ".docx", ".jpg", ".jpeg", ".png", ".webp", ".md", ".txt"];

function formatBytes(b) {
  if (b < 1024) return b + " B";
  if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
  return (b / 1048576).toFixed(1) + " MB";
}

function FileRow({ file }) {
  const s = STATUS[file.status] || STATUS.pending;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px", borderBottom: "1px solid #1a1f2e",
      transition: "background 0.2s",
      background: file.status === "error" ? "rgba(239,68,68,0.04)" : "transparent"
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "'IBM Plex Mono', monospace" }}>
          {file.name}
        </div>
        {file.error && (
          <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>{file.error}</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap" }}>{formatBytes(file.size)}</div>
      <div style={{
        fontSize: 11, fontWeight: 600, letterSpacing: "0.05em",
        color: s.color, whiteSpace: "nowrap", minWidth: 72, textAlign: "right",
        textTransform: "uppercase"
      }}>
        {file.status === "uploading" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Spinner size={10} color={s.color} /> {s.label}
          </span>
        ) : s.label}
      </div>
    </div>
  );
}

function Spinner({ size = 14, color = "#d4a017" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite" }}>
      <circle cx="12" cy="12" r="10" fill="none" stroke={color} strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" strokeLinecap="round" />
    </svg>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{
      background: "#0d1117", border: "1px solid #1e2738",
      borderRadius: 8, padding: "14px 20px", flex: 1, minWidth: 0
    }}>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || "#e2e8f0", fontFamily: "'IBM Plex Mono', monospace" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.08em", marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function LexisUploader() {
  const [files, setFiles] = useState([]);
  const [running, setRunning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const folderRef = useRef(null);
  const fileRef = useRef(null);
  const abortRef = useRef(false);

  const addFiles = useCallback((incoming) => {
    const filtered = Array.from(incoming).filter(f => {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      return ALLOWED_EXT.includes(ext);
    });
    setFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name + f.size));
      const fresh = filtered
        .filter(f => !existingNames.has(f.name + f.size))
        .map(f => ({ id: crypto.randomUUID(), file: f, name: f.name, size: f.size, status: "pending", error: null }));
      return [...prev, ...fresh];
    });
    return filtered.length;
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const items = e.dataTransfer.items;
    const allFiles = [];
    if (items) {
      for (const item of items) {
        if (item.kind === "file") allFiles.push(item.getAsFile());
      }
    } else {
      for (const f of e.dataTransfer.files) allFiles.push(f);
    }
    addFiles(allFiles);
  }, [addFiles]);

  const uploadFile = async (entry) => {
    setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "uploading" } : f));
    const form = new FormData();
    form.append("file", entry.file);
    // Extract folder_name from webkitRelativePath for folder grouping
    // webkitRelativePath looks like "ParentFolder/subfolder/file.pdf"
    // We want the immediate parent folder: "ParentFolder"
    const rawPath = entry.file.webkitRelativePath || "";
    if (rawPath) {
      const parts = rawPath.split("/");
      // parts[0] = root folder, parts[1] = immediate subfolder (or file if no subfolder)
      // If file is directly in root folder, use root folder name
      const folderName = parts.length > 2 ? parts[1] : (parts.length > 1 ? parts[0] : "");
      if (folderName) {
        form.append("folder_name", folderName);
      }
    }
    try {
      const res = await fetch(`${API_BASE}/lexis/upload/single`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "Server error");
        throw new Error(txt.slice(0, 120));
      }
      setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "processing" } : f));
      await new Promise(r => setTimeout(r, 2000));
      setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "done" } : f));
    } catch (err) {
      setFiles(prev => prev.map(f => f.id === entry.id ? { ...f, status: "error", error: err.message } : f));
    }
  };

  const runUpload = async () => {
    abortRef.current = false;
    setRunning(true);
    setShowSummary(false);
    const pending = files.filter(f => f.status === "pending" || f.status === "error");
    const numBatches = Math.ceil(pending.length / BATCH_SIZE);
    setTotalBatches(numBatches);

    for (let i = 0; i < pending.length; i += BATCH_SIZE) {
      if (abortRef.current) break;
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      setCurrentBatch(batchNum);
      const batch = pending.slice(i, i + BATCH_SIZE);
      await Promise.all(batch.map(uploadFile));
    }
    setRunning(false);
    setCurrentBatch(0);
    setTotalBatches(0);
    setShowSummary(true);
  };

  const stopUpload = () => { abortRef.current = true; };

  const clearDone = () => setFiles(prev => prev.filter(f => f.status !== "done"));
  const clearAll = () => { abortRef.current = true; setFiles([]); setRunning(false); setShowSummary(false); };
  const retryFailed = () => {
    setShowSummary(false);
    setFiles(prev => prev.map(f => f.status === "error" ? { ...f, status: "pending", error: null } : f));
    // Auto-start upload after retry
    setTimeout(() => {
      setFiles(prev => {
        const hasPending = prev.some(f => f.status === "pending");
        if (hasPending) {
          // Trigger upload by setting running and calling runUpload
          setRunning(true);
          setShowSummary(false);
          const pending = prev.filter(f => f.status === "pending" || f.status === "error");
          const numBatches = Math.ceil(pending.length / BATCH_SIZE);
          setTotalBatches(numBatches);
          // We need to trigger the actual upload loop
          // Since we can't call runUpload from setState, we'll use a different approach
        }
        return prev;
      });
    }, 0);
  };

  const counts = files.reduce((acc, f) => { acc[f.status] = (acc[f.status] || 0) + 1; return acc; }, {});
  const total = files.length;
  const done = counts.done || 0;
  const errors = counts.error || 0;
  const pendingCount = (counts.pending || 0) + (counts.uploading || 0) + (counts.processing || 0);
  const progress = total ? Math.round((done / total) * 100) : 0;

  // Auto-start upload when files are added via folder select
  const handleFolderSelect = (e) => {
    const count = addFiles(e.target.files);
    if (count > 0) {
      // Auto-start upload after files are added
      setTimeout(() => {
        setFiles(prev => {
          const hasPending = prev.some(f => f.status === "pending");
          if (hasPending && !running) {
            // Trigger the upload process
            setRunning(true);
            setShowSummary(false);
            const pending = prev.filter(f => f.status === "pending" || f.status === "error");
            const numBatches = Math.ceil(pending.length / BATCH_SIZE);
            setTotalBatches(numBatches);
            // Run upload in a microtask
            Promise.resolve().then(async () => {
              abortRef.current = false;
              for (let i = 0; i < pending.length; i += BATCH_SIZE) {
                if (abortRef.current) break;
                const batchNum = Math.floor(i / BATCH_SIZE) + 1;
                setCurrentBatch(batchNum);
                const batch = pending.slice(i, i + BATCH_SIZE);
                // Re-fetch current files for this batch since state may have changed
                const batchEntries = batch.map(f => ({ ...f }));
                await Promise.all(batchEntries.map(uploadFile));
              }
              setRunning(false);
              setCurrentBatch(0);
              setTotalBatches(0);
              setShowSummary(true);
            });
          }
          return prev;
        });
      }, 100);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "#080b12",
      fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      color: "#e2e8f0", display: "flex", flexDirection: "column",
      alignItems: "center", padding: "40px 20px"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: #0d1117; } ::-webkit-scrollbar-thumb { background: #2d3748; border-radius: 4px; }
        .upbtn { cursor: pointer; border: none; border-radius: 6px; font-family: inherit; font-size: 13px; font-weight: 600; padding: 10px 18px; transition: all 0.15s; letter-spacing: 0.03em; }
        .upbtn:disabled { opacity: 0.4; cursor: not-allowed; }
        .upbtn-primary { background: #d4a017; color: #080b12; }
        .upbtn-primary:not(:disabled):hover { background: #e8b420; transform: translateY(-1px); }
        .upbtn-ghost { background: transparent; color: #64748b; border: 1px solid #1e2738; }
        .upbtn-ghost:not(:disabled):hover { color: #e2e8f0; border-color: #334155; }
        .upbtn-danger { background: transparent; color: #ef4444; border: 1px solid #2d1515; }
        .upbtn-danger:not(:disabled):hover { background: rgba(239,68,68,0.08); }
        .dropzone { border: 2px dashed #1e2738; border-radius: 12px; transition: all 0.2s; }
        .dropzone.active { border-color: #d4a017; background: rgba(212,160,23,0.04); }
        .dropzone:hover { border-color: #2d3748; }
      `}</style>

      <div style={{ width: "100%", maxWidth: 780 }}>
        {/* Header */}
        <div style={{ marginBottom: 32, animation: "fadeIn 0.4s ease" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: "#d4a017", letterSpacing: "0.12em", textTransform: "uppercase" }}>LEXIS</span>
            <span style={{ fontSize: 11, color: "#2d3748" }}>▸</span>
            <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: "#4a5568", letterSpacing: "0.08em" }}>Knowledge Base Uploader</span>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, margin: 0, color: "#f1f5f9", letterSpacing: "-0.02em" }}>
            Document Ingestion
          </h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#4a5568" }}>
            Select files or folder — PDF, DOCX, images, markdown accepted
          </p>
        </div>

        {/* Stats row */}
        {total > 0 && (
          <div style={{ display: "flex", gap: 10, marginBottom: 20, animation: "fadeIn 0.3s ease" }}>
            <StatBox label="Total" value={total} />
            <StatBox label="Indexed" value={done} color="#22c55e" />
            <StatBox label="Pending" value={pendingCount} color="#d4a017" />
            <StatBox label="Failed" value={errors} color={errors > 0 ? "#ef4444" : "#4a5568"} />
          </div>
        )}

        {/* Progress bar */}
        {total > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#4a5568", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace" }}>
              <span>
                {running && currentBatch > 0 ? `Batch ${currentBatch}/${totalBatches} · ${done}/${total} files` : "Progress"}
              </span>
              <span>{progress}% — {done}/{total}</span>
            </div>
            <div style={{ height: 4, background: "#1a1f2e", borderRadius: 2 }}>
              <div style={{
                height: "100%", borderRadius: 2,
                background: errors > 0 ? "linear-gradient(90deg, #22c55e, #ef4444)" : "#d4a017",
                width: `${progress}%`, transition: "width 0.4s ease"
              }} />
            </div>
          </div>
        )}

        {/* Drop zone */}
        <div
          className={`dropzone ${dragging ? "active" : ""}`}
          style={{ padding: "40px 24px", textAlign: "center", marginBottom: 16, cursor: "pointer" }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
        >
          <div style={{ fontSize: 32, marginBottom: 10 }}>📂</div>
          <div style={{ fontSize: 14, color: "#94a3b8", marginBottom: 4 }}>
            Drop files here, or <span style={{ color: "#d4a017", textDecoration: "underline" }}>click to browse</span>
          </div>
          <div style={{ fontSize: 11, color: "#2d3748" }}>PDF · DOCX · JPG · PNG · MD · TXT</div>
        </div>

        {/* Hidden inputs */}
        <input ref={fileRef} type="file" multiple accept={ALLOWED_EXT.join(",")} style={{ display: "none" }}
          onChange={e => addFiles(e.target.files)} />
        <input ref={folderRef} type="file" multiple accept={ALLOWED_EXT.join(",")}
          // @ts-ignore
          webkitdirectory="" directory=""
          style={{ display: "none" }}
          onChange={handleFolderSelect} />

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          <button className="upbtn upbtn-ghost" onClick={() => folderRef.current?.click()} disabled={running}>
            📁 Select Folder
          </button>
          <button className="upbtn upbtn-ghost" onClick={() => fileRef.current?.click()} disabled={running}>
            📄 Add Files
          </button>
          <div style={{ flex: 1 }} />
          {errors > 0 && !running && (
            <button className="upbtn upbtn-ghost" onClick={retryFailed}>↺ Retry Failed</button>
          )}
          {done > 0 && !running && (
            <button className="upbtn upbtn-ghost" onClick={clearDone}>✕ Clear Indexed</button>
          )}
          {total > 0 && !running && (
            <button className="upbtn upbtn-danger" onClick={clearAll}>Clear All</button>
          )}
          {running ? (
            <button className="upbtn upbtn-danger" onClick={stopUpload}>⏹ Stop</button>
          ) : (
            <button className="upbtn upbtn-primary" onClick={runUpload}
              disabled={!files.some(f => f.status === "pending" || f.status === "error")}>
              {running ? <><Spinner size={12} color="#080b12" /> Uploading…</> : `▶ Upload ${files.filter(f => f.status === "pending").length || ""} Files`}
            </button>
          )}
        </div>

        {/* Summary report */}
        {showSummary && (
          <div style={{
            background: "#0d1117", border: "1px solid #1e2738",
            borderRadius: 10, padding: "20px 24px", marginBottom: 20,
            animation: "fadeIn 0.4s ease"
          }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600, color: "#f1f5f9" }}>
              Upload Summary
            </h3>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, color: "#22c55e" }}>
                ✓ {done} file{done !== 1 ? "s" : ""} indexed successfully
              </div>
              {errors > 0 && (
                <div style={{ fontSize: 14, color: "#ef4444" }}>
                  ✗ {errors} file{errors !== 1 ? "s" : ""} failed
                </div>
              )}
            </div>
            {errors > 0 && (
              <div style={{
                borderTop: "1px solid #1a1f2e", paddingTop: 12
              }}>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8, fontFamily: "'IBM Plex Mono', monospace" }}>
                  Failed files:
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {files.filter(f => f.status === "error").map(f => (
                    <div key={f.id} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      padding: "6px 0", borderBottom: "1px solid #1a1f2e", fontSize: 12
                    }}>
                      <span style={{ color: "#e2e8f0", fontFamily: "'IBM Plex Mono', monospace", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.name}
                      </span>
                      <span style={{ color: "#ef4444", marginLeft: 12, fontSize: 11, maxWidth: 200, textAlign: "right" }}>
                        {f.error || "Unknown error"}
                      </span>
                    </div>
                  ))}
                </div>
                <button className="upbtn upbtn-ghost" style={{ marginTop: 12 }} onClick={retryFailed}>
                  ↺ Retry Failed Files Only
                </button>
              </div>
            )}
          </div>
        )}

        {/* File list */}
        {files.length > 0 && (
          <div style={{
            background: "#0d1117", border: "1px solid #1a1f2e",
            borderRadius: 10, overflow: "hidden", animation: "fadeIn 0.3s ease"
          }}>
            <div style={{
              padding: "10px 14px", borderBottom: "1px solid #1a1f2e",
              display: "flex", justifyContent: "space-between", alignItems: "center"
            }}>
              <span style={{ fontSize: 11, color: "#4a5568", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {total} document{total !== 1 ? "s" : ""}
              </span>
              <span style={{ fontSize: 11, color: "#4a5568", fontFamily: "'IBM Plex Mono', monospace" }}>
                Batch size: {BATCH_SIZE} parallel
              </span>
            </div>
            <div style={{ maxHeight: 440, overflowY: "auto" }}>
              {files.map(f => <FileRow key={f.id} file={f} />)}
            </div>
          </div>
        )}

        {files.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#2d3748", fontSize: 13 }}>
            No documents loaded yet
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 24, fontSize: 11, color: "#1e2738", textAlign: "center", fontFamily: "'IBM Plex Mono', monospace" }}>
          lesia.special-equip.com · LEXIS Knowledge Base · {BATCH_SIZE} files per batch · ~45s per document
        </div>
      </div>
    </div>
  );
}
